import React, { useEffect, useState } from 'react';
import { Route, Routes, Link, useLocation, Navigate } from 'react-router-dom';
import { Typography, Box, AppBar, Toolbar, Grid2 } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import palette from './assets/sass/palette.scss';
import Register from './Register';
import Settings from './Settings';
import PaymentSuccess from './PaymentSuccess';
import Footer from './Footer';
import Terms from './Terms';
import Activity from './Activity';
import Privacy from './Privacy';
import Home from './Home';
import AuthAction from './AuthAction';
import ForgotPassword from './ForgotPassword';
import GameForm from './GameForm';
import GameView from './GameView';
import GamesInvite from './GamesInvite';
import Dashboard from './Dashboard';
import ProtectedRoute from './components/ProtectedRoute';
import SignIn from './SignIn';
import SignInWhatsApp from './SignInWhatsApp';
import SignInEmail from './SignInEmail';
import FinishOnboarding from './FinishOnboarding';
import ProfileSidebar from './components/ProfileSidebar';
import NavMenu from './components/NavMenu';
import SharedInvitation from './SharedInvitation';
import { HelmetProvider } from 'react-helmet-async';
import { useAuth } from './contexts/AuthContext';
import { useFirebase } from './contexts/FirebaseContext';
import ErrorBoundary from './components/ErrorBoundary';
import { MfaSetup } from './components/mfa/MfaSetup';
import { MfaDisable } from './components/mfa/MfaDisable';
import { trackPageView } from './utilities/hotjarUtils';
import { useParams } from 'react-router-dom';
import PageTitle from './components/PageTitle';
import Logo from './components/Logo';
import FindAGame from './FindAGame';
import { useNavigate } from 'react-router-dom';
import LoadingScreen from './components/LoadingScreen';
import GameCreated from './GameCreated';

function App() {
  const { currentUser, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMediumlUp = useMediaQuery(theme.breakpoints.up('md'));
  const [showProfile, setShowProfile] = useState(false);

  // Track page changes with Hotjar
  useEffect(() => {
    trackPageView(location.pathname);
  }, [location]);

  const { auth } = useFirebase();

  if (loading) {
    return <LoadingScreen />;
  }

  function GameFormWrapper() {
    const { id } = useParams();
    return <GameForm key={id} />;
  }

  const handleSignOut = async () => {
    try {
      await auth.signOut();
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleAvatarClick = () => {
    setShowProfile(!showProfile);
  };

  return (
    <HelmetProvider>
      <Grid2
        container
        spacing={0}
        direction="row-reverse"
        sx={{
          justifyContent: 'center',
          alignItems: 'stretch',
          minHeight: '100vh',
          margin: '0 auto',
          backgroundColor: palette.white,
        }}
      >
        <Grid2 size={{ xs: 12, md: currentUser ? 9 : 12 }}>
          <AppBar position="static" color="transparent" sx={{ alignItems: { md: 'flex-end' }, px: { xs: 2, sm: 3, md: 0 } }}>
            <Toolbar sx={{ maxWidth: '1060px', mx: 'auto', width: '100%', px: { xs: 0, sm: 3, md: 6} }}>
              {currentUser && location.pathname !== '/onboarding' ? (
                <>
                  <PageTitle />
                  <Logo showOnMobile={true} />
                  <NavMenu handleAvatarClick={handleAvatarClick} />
                </>
              ) : currentUser && location.pathname === '/onboarding' ? (
                <>
                  <Box sx={{ display: 'flex', flexGrow: 1 }}>
                    <Logo />
                    <Logo showOnMobile={true} />
                  </Box>
                  <Typography
                    variant="headerLink"
                    onClick={handleSignOut}
                    sx={{ cursor: 'pointer' }}
                  >
                    Sign Out
                  </Typography>
                </>
              ) : (
                <>
                  <Box sx={{ display: 'flex', flexGrow: 1 }}>
                    <Logo />
                    <Logo showOnMobile={true} />
                  </Box>
                  <Typography variant="headerLink" component={Link} to="/signin">
                    Sign In / Register
                  </Typography>
                </>
              )}
            </Toolbar>
          </AppBar>

          <Box sx={{ my: 4, m: '0px !important', px: { xs: '0' } }}>
            {/* Unprotected Routes */}
            <PageTitle showOnMobile={true} />
            <ErrorBoundary>
              <Routes>
              <Route
                path="/"
                element={currentUser ? <Navigate to="/dashboard" replace /> : <Home />}
              />
              <Route path="/signin" element={<SignIn />} />
              <Route path="/signin/whatsapp" element={<SignInWhatsApp />} />
              <Route path="/signin/email" element={<SignInEmail />} />
              <Route path="/onboarding" element={<FinishOnboarding />} />
              <Route path="/register" element={<Register />} />
              <Route path="/auth/action" element={<AuthAction />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/payment-success" element={<PaymentSuccess />} />
              <Route path="/invitation/:shareCode" element={<SharedInvitation />} />
              <Route path="/find-a-game" element={<FindAGame />} />

              {/* Protected Routes */}

              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/new-game"
                element={
                  <ProtectedRoute>
                    <GameFormWrapper />
                  </ProtectedRoute>
                }
              />

              <Route path="/games/:id" element={<GameView />} />

              <Route
                path="/edit-games/:id"
                element={
                  <ProtectedRoute>
                    <GameFormWrapper />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/games/:id/invite"
                element={
                  <ProtectedRoute>
                    <GamesInvite />
                  </ProtectedRoute>
                }
              />

<Route
                path="/games/:id/invite-new"
                element={
                  <ProtectedRoute>
                    <GamesInvite isNewGame={true} />
                  </ProtectedRoute>
                }
              />

<Route
                path="/games/:id/created"
                element={
                  <ProtectedRoute>
                    <GameCreated />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/activity"
                element={
                  <ProtectedRoute>
                    <Activity />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/settings"
                element={
                  <ProtectedRoute>
                    <Settings />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/settings/mfa/setup"
                element={
                  <ProtectedRoute>
                    <MfaSetup />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/settings/mfa/disable"
                element={
                  <ProtectedRoute>
                    <MfaDisable />
                  </ProtectedRoute>
                }
              />
              </Routes>
            </ErrorBoundary>
          </Box>
          <Footer />
        </Grid2>
        {currentUser && location.pathname !== '/onboarding' && isMediumlUp && (
          <Grid2
            size={{ xs: 12, md: currentUser ? 3 : 0 }}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              backgroundColor: palette.darkGreen,
              alignItems: 'flex-end',
              borderRadius: { xs: 0, md: '0 100px 0 0' },
            }}
          >
            <ProfileSidebar />
          </Grid2>
        )}
      </Grid2>
      {!isMediumlUp && showProfile && (
        <Box
          sx={{
            width: '100%',
            height: '100vh',
            position: 'fixed',
            overflow: 'scroll',
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            zIndex: 100,
          }}
        >
          <ProfileSidebar showClose={true} handleClose={handleAvatarClick} />
        </Box>
      )}
    </HelmetProvider>
  );
}

export default App;
